import React from 'react'
import { Navigate, Route } from 'react-router-dom'

import { featuresStatuses } from '../config/brands'
import { profileTabSettingsPersonal, promoTabSettings } from '../shared/navigation/NavMenuData'
import { routes } from '../shared/routes'
import { lazyLoadPageWithRetry } from '../shared/utils'
import { AuthWrapper } from './auth/base/containers/AuthWrapper'
import { renderPostbacks } from './postbacks'
import { PromoBannersById } from './promo/banners/:id'

const OnBoarding = lazyLoadPageWithRetry(() => import('./onboarding'))
const Dashboard = lazyLoadPageWithRetry(() => import('./dashboard'))

const Promo = lazyLoadPageWithRetry(() => import('./promo'))

const Offers = lazyLoadPageWithRetry(() => import('./offers'))
const OffersByAdvertisers = lazyLoadPageWithRetry(() => import('./offers/advertisers'))
const Offer = lazyLoadPageWithRetry(() => import('./offers/offer'))

const StatisticsGeneral = lazyLoadPageWithRetry(() => import('./statistics/general'))
const StatisticsByUsers = lazyLoadPageWithRetry(() => import('./statistics/users'))
const StatisticsReferral = lazyLoadPageWithRetry(() => import('./statistics/referral'))

const Payments = lazyLoadPageWithRetry(() => import('./payments'))
const Top = lazyLoadPageWithRetry(() => import('./top'))

const Contests = lazyLoadPageWithRetry(() => import('./contests'))
const ContestsType = lazyLoadPageWithRetry(() => import('./contests/_contestsType'))
const Contest = lazyLoadPageWithRetry(() => import('./contests/contest'))
const ContestTab = lazyLoadPageWithRetry(() => import('./contests/contest/_contestTab'))

const LoyaltyProgram = lazyLoadPageWithRetry(() => import('./loyalty-program'))
const Achievements = lazyLoadPageWithRetry(() => import('./loyalty-program/achievements'))

const ProfileSettings = lazyLoadPageWithRetry(() => import('./profile/settings'))
const ProfileSecurity = lazyLoadPageWithRetry(() => import('./profile/security'))
const ProfileKYC = lazyLoadPageWithRetry(() => import('./profile/kyc'))

const SignIn = lazyLoadPageWithRetry(() => import('./auth/signin'))
const SignUp = lazyLoadPageWithRetry(() => import('./auth/signup'))
const TwoFactorAuth = lazyLoadPageWithRetry(() => import('./auth/two-factor-auth'))
const ResetPasswordRequest = lazyLoadPageWithRetry(() => import('./auth/reset-password-request'))
const ResetPassword = lazyLoadPageWithRetry(() => import('./auth/reset-password'))
const Activate = lazyLoadPageWithRetry(() => import('./auth/activate'))

const DownloadAuthenticatorApp = lazyLoadPageWithRetry(() => import('./download-authenticator-app'))
const PaymentAccountConfirm = lazyLoadPageWithRetry(() => import('./payment-account-confirm'))
const PrivacyPolicy = lazyLoadPageWithRetry(() => import('./privacy-policy'))
const TermsAndConditions = lazyLoadPageWithRetry(() => import('./terms-and-conditions'))
const ByTransferTokenPage = lazyLoadPageWithRetry(() => import('./auth/by-transfer-token'))

export const renderRoutingProtected = () => (
  <>
    <Route element={<OnBoarding />} path={routes.ROUTES_ONBOARDING.onboarding} />
    {featuresStatuses.isDashboardPageEnabled && <Route element={<Dashboard />} path={routes.ROUTES_DASHBOARD.root} />}
    {featuresStatuses.isPromoPageEnabled && (
      <>
        <Route element={<Promo />} path={routes.ROUTES_PROMO.promoTab} />
        <Route element={<PromoBannersById />} path={routes.ROUTES_PROMO.promoTabById.replace(':tabValue', promoTabSettings.banners)} />
      </>
    )}

    <Route>
      <Route element={<Offers />} path={routes.ROUTES_OFFERS.offers} />
      <Route element={<OffersByAdvertisers />} path={routes.ROUTES_OFFERS.offersAdvertisers} />
      <Route element={<Offer />} path={routes.ROUTES_OFFERS.offerTab} />
      <Route element={<Offer />} path={routes.ROUTES_OFFERS.offerModel} />
    </Route>

    <Route>
      <Route element={<StatisticsGeneral />} path={routes.ROUTES_STATISTICS.general} />
      <Route element={<StatisticsByUsers />} path={routes.ROUTES_STATISTICS.users} />
    </Route>
    <Route element={<Payments />} path={routes.ROUTES_PAYMENTS.paymentsTab} />
    <Route element={featuresStatuses.isTop10PageEnabled ? <Top /> : <Navigate to={routes.ROUTES_ERROR['404']} />} path={routes.ROUTES_TOP.root} />

    <Route>
      <Route
        element={featuresStatuses.isContestsPageEnabled ? <Contests /> : <Navigate to={routes.ROUTES_ERROR['404']} />}
        path={routes.ROUTES_CONTESTS.root}
      >
        <Route element={<Navigate to={routes.ROUTES_ERROR['404']} />} path={routes.ROUTES_CONTESTS.root} />
        <Route element={<ContestsType />} path={routes.ROUTES_CONTESTS.tabsPath} />
      </Route>
      <Route
        element={featuresStatuses.isContestsPageEnabled ? <Contest /> : <Navigate to={routes.ROUTES_ERROR['404']} />}
        path={routes.ROUTES_CONTESTS.contestPath}
      >
        <Route element={<ContestTab />} path={routes.ROUTES_CONTESTS.contestTabsPath} />
      </Route>
    </Route>

    {featuresStatuses.isSubAffiliatePageEnabled && <Route element={<StatisticsReferral />} path={routes.ROUTES_SUB_AFFILIATE.subAffiliate} />}
    {featuresStatuses.isLoyaltyProgramPageEnabled && (
      <>
        <Route element={<LoyaltyProgram />} path={routes.ROUTES_LOYALTY_PROGRAM.root} />
        <Route element={<Achievements />} path={routes.ROUTES_LOYALTY_PROGRAM.achievements} />
      </>
    )}

    {renderPostbacks()}

    <Route>
      {featuresStatuses.isKYCEnabled && <Route element={<ProfileKYC />} path={routes.ROUTES_PROFILE.profileKYC} />}
      <Route element={<ProfileSecurity />} path={routes.ROUTES_PROFILE.profileSecurity} />
      <Route element={<Navigate to={profileTabSettingsPersonal} />} path={routes.ROUTES_PROFILE.profileSettings} />
      <Route element={<ProfileSettings />} path={routes.ROUTES_PROFILE.profileSettingsTab} />
    </Route>
  </>
)

export const renderRoutingGuest = () => (
  <>
    <Route element={<AuthWrapper />} path="/auth">
      <Route element={<SignIn />} path={routes.ROUTES_AUTH.signIn} />
      <Route element={<SignUp />} path={routes.ROUTES_AUTH.signUp} />
      <Route element={<TwoFactorAuth />} path={routes.ROUTES_AUTH.twoFA} />
      <Route element={<ResetPasswordRequest />} path={routes.ROUTES_AUTH.resetPasswordRequest} />
      <Route element={<ResetPassword />} path={routes.ROUTES_AUTH.resetPassword} />
      <Route element={<Activate />} path={routes.ROUTES_AUTH.activate} />
      <Route element={<Navigate to={routes.ROUTES_AUTH.signIn} />} path="*" />
    </Route>
  </>
)

export const renderSharedRoutes = () => (
  <>
    <Route element={<DownloadAuthenticatorApp />} path={routes.ROUTES_SHARED.authenticatorRedirect} />
    <Route element={<PaymentAccountConfirm />} path={routes.ROUTES_SHARED.paymentAccountConfirm} />
    <Route element={<PrivacyPolicy />} path={routes.ROUTES_SHARED.privacyPolicy} />
    <Route element={<TermsAndConditions />} path={routes.ROUTES_SHARED.termsAndConditions} />
    <Route element={<ByTransferTokenPage />} path={routes.ROUTES_SHARED.byTransferToken} />
  </>
)
